/**
 * Where the card docks and how the canvas is framed, for one scene on one
 * screen. Pure geometry: the runtime measures once the screen has settled,
 * this decides, and the answer stands until the scene is set again.
 */

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

export type Dock =
  /** Middle of the window. */
  | "center"
  /** Right of the stage, wide, with the canvas kept in view beside it. */
  | "splash"
  /** Right edge of the stage, narrow. */
  | "stageRight"
  /** As `stageRight`, moved left to clear the popover the spotlit target opens. */
  | "besideTarget";

export interface Geometry {
  viewport: Size;
  /** The canvas pane on screen. */
  pane: Box;
  /** The open chat window. */
  chat: Box | null;
  rightPane: Box | null;
  target: Box | null;
  /** The canvas toolbar: along the bottom, or down the left on narrow panes. */
  toolbar: Box | null;
  /** The canvas's own controls, top right of the stage. */
  controls: Box | null;
}

/** Dimmed chrome the card stands on top of. */
export type Covered = "controls" | "tools";

export interface CardPlace {
  left: number;
  top: number;
  width: number;
}

export interface Frame {
  x: number;
  y: number;
  zoom: number;
}

export const INSET = 16;
export const GAP = 16;
/** Clear space around what the canvas frames. */
export const FRAME_PAD = 32;
const COMPACT_W = 520;
const NARROW_W = 312;
const MIN_W = 280;
const SPLASH_FRACTION = 0.6;
const SPLASH_MAX_W = 1280;
/** The least canvas a splash card leaves beside it. */
const SPLASH_CANVAS_MIN = 448;
/** What the publish popover covers below and left of its button. */
const POPOVER_W = 360;
const MIN_REGION = 64;
/**
 * The height kept for a card docked under the canvas, so every step's card
 * leaves the same room above it and the frame holds from step to step. The
 * cards beside the publish popover run taller.
 */
const UNDER_CARD_H = 300;
const UNDER_TARGET_CARD_H = 360;
/** On a wider stage the scene plays on a band this wide, in its middle. */
const BAND_MAX_W = 1600;
/** How much larger the canvas must show for the card to go under it. */
const PREFER_BESIDE = 1.15;

const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));
const right = (b: Box) => b.left + b.width;
const bottom = (b: Box) => b.top + b.height;
const overlaps = (a: Box, b: Box) =>
  a.left < right(b) &&
  right(a) > b.left &&
  a.top < bottom(b) &&
  bottom(a) > b.top;

/** The canvas the scene plays on: the pane, less the chat's column. */
export function stageOf({ pane, chat }: Pick<Geometry, "pane" | "chat">): Box {
  if (!chat) return pane;
  const edge = clamp(chat.left, pane.left, right(pane));
  return { ...pane, width: edge - pane.left };
}

/**
 * Where a scene plays: the stage, or for the narrow card its middle band, so
 * a very wide window does not part the card from what it is about.
 */
function playOf(dock: Dock, g: Geometry): Box {
  const stage = stageOf(g);
  if (dock === "center" || dock === "splash" || stage.width <= BAND_MAX_W)
    return stage;
  return {
    ...stage,
    left: Math.round(stage.left + (stage.width - BAND_MAX_W) / 2),
    width: BAND_MAX_W,
  };
}

/** Where a card on the stage ends: the stage's edge, or the popover's. */
function cardEdge(dock: Dock, g: Geometry): number {
  const stage = right(playOf(dock, g));
  return dock === "besideTarget" && g.target
    ? Math.min(stage, right(g.target) - POPOVER_W)
    : stage;
}

/** How wide the card is: its dock's width, less on a stage too narrow for it. */
export function cardWidth(dock: Dock, g: Geometry): number {
  const { viewport } = g;
  const safe = Math.max(0, viewport.width - INSET * 2);
  const stage = playOf(dock, g);
  if (dock === "center") return Math.min(COMPACT_W, safe);
  if (dock === "splash") {
    const wide = Math.min(
      Math.round(viewport.width * SPLASH_FRACTION),
      SPLASH_MAX_W,
    );
    const room = stage.width - GAP * 2 - SPLASH_CANVAS_MIN;
    return Math.min(safe, Math.max(COMPACT_W, Math.min(wide, room)));
  }
  const room = cardEdge(dock, g) - stage.left - GAP * 2;
  return Math.min(safe, Math.max(MIN_W, Math.min(NARROW_W, Math.floor(room))));
}

interface Region {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** How large `box` shows in a region; 0 when the region is too small to use. */
function zoomIn(region: Region, box: Box, maxZoom: number): number {
  const w = region.right - region.left;
  const h = region.bottom - region.top;
  if (w < MIN_REGION || h < MIN_REGION) return 0;
  return Math.min(maxZoom, w / box.width, h / box.height);
}

/** The viewport that shows `box` centered in a region at a zoom. */
function centered(region: Region, box: Box, zoom: number, pane: Box): Frame {
  const left = (region.left + region.right) / 2 - (box.width * zoom) / 2;
  const top = (region.top + region.bottom) / 2 - (box.height * zoom) / 2;
  return {
    x: Math.round(left - pane.left - box.left * zoom),
    y: Math.round(top - pane.top - box.top * zoom),
    zoom,
  };
}

export interface Composition {
  place: CardPlace;
  /** Null when the scene frames nothing, or no usable room is left. */
  frame: Frame | null;
  /** Chrome under the card: put away whole, never left cut by its edge. */
  covers: Covered[];
}

/**
 * The card's place and the canvas's frame, decided together. A card on the
 * stage stands at its right edge, centered, with `box` (canvas coordinates)
 * centered in what is left beside it. On a stage too narrow for that, the
 * card goes under the box, both in the middle, when that shows the box
 * clearly larger. The frame never puts the box under the card, the chat,
 * the right pane or a toolbar, nor off the stage. The card stands clear of
 * the canvas's toolbar and controls where it fits; what it cannot clear it
 * covers, and says so.
 */
export function compose({
  dock,
  geometry: g,
  card,
  box,
  maxZoom,
}: {
  dock: Dock;
  geometry: Geometry;
  /** The card as measured at `cardWidth(dock, geometry)`. */
  card: Size;
  /** What the scene frames; null leaves the viewport alone. */
  box: Box | null;
  maxZoom: number;
}): Composition {
  const { viewport, pane, toolbar, controls } = g;
  const width = Math.min(card.width, viewport.width - INSET * 2);
  const height = Math.min(card.height, viewport.height - INSET * 2);
  const fit = (left: number, top: number): CardPlace => ({
    left: Math.round(clamp(left, INSET, viewport.width - INSET - width)),
    top: Math.round(clamp(top, INSET, viewport.height - INSET - height)),
    width,
  });
  const stage = playOf(dock, g);
  const tools = toolbar && toolbar.width >= toolbar.height ? toolbar : null;
  const composed = (place: CardPlace, frame: Frame | null): Composition => {
    const under = (b: Box | null) =>
      !!b &&
      place.left < right(b) &&
      place.left + width > b.left &&
      place.top < bottom(b) &&
      place.top + height > b.top;
    const covers: Covered[] = [];
    if (under(controls)) covers.push("controls");
    if (under(tools)) covers.push("tools");
    return { place, frame, covers };
  };

  const free: Region = {
    left: stage.left + FRAME_PAD,
    top: stage.top + FRAME_PAD,
    right: right(stage) - FRAME_PAD,
    bottom: bottom(stage) - FRAME_PAD,
  };
  // The card's own floor: above a toolbar that runs along the bottom.
  let floor = bottom(stage) - GAP;
  if (toolbar && toolbar.width > 0) {
    if (toolbar.height > toolbar.width) {
      free.left = Math.max(free.left, right(toolbar) + FRAME_PAD);
    } else {
      free.bottom = Math.min(free.bottom, toolbar.top - FRAME_PAD);
      floor = Math.min(floor, toolbar.top - GAP);
    }
  }
  const framed = (region: Region): Frame | null => {
    if (!box) return null;
    const zoom = zoomIn(region, box, maxZoom);
    return zoom > 0 ? centered(region, box, zoom, pane) : null;
  };

  if (dock === "center") {
    return composed(
      fit((viewport.width - width) / 2, (viewport.height - height) / 2),
      framed(free),
    );
  }

  const edge = cardEdge(dock, g) - GAP;
  const left = edge - width;
  const across = (b: Box | null): b is Box =>
    !!b && left < right(b) && left + width > b.left;
  // Centered on the stage, then above the toolbar and below the controls
  // where there is room for that.
  let lowest = bottom(stage) - GAP - height;
  if (across(tools) && tools.top - GAP - height >= stage.top + GAP)
    lowest = Math.min(lowest, tools.top - GAP - height);
  let top = Math.min(stage.top + (stage.height - height) / 2, lowest);
  if (across(controls)) {
    const below = bottom(controls) + GAP;
    if (top < below && below <= lowest) top = below;
  }
  const beside = {
    place: fit(left, top),
    region: { ...free, right: Math.min(free.right, left - FRAME_PAD) },
  };
  if (!box || dock === "splash")
    return composed(beside.place, framed(beside.region));

  // Under the canvas the card stands in the middle, its top held where the
  // kept room starts.
  const kept = dock === "besideTarget" ? UNDER_TARGET_CARD_H : UNDER_CARD_H;
  const room = floor - Math.max(kept, height);
  const above = { ...free, bottom: Math.min(free.bottom, room - FRAME_PAD) };
  // The box spans the stage here, so it starts below the controls when it
  // would otherwise run under them.
  const shown = framed(above);
  const under = {
    place: fit((free.left + free.right - width) / 2, room),
    region:
      controls && shown && overlaps(onScreen(box, shown, pane), controls)
        ? { ...above, top: Math.max(above.top, bottom(controls) + GAP) }
        : above,
  };
  const taken =
    zoomIn(under.region, box, maxZoom) >
    zoomIn(beside.region, box, maxZoom) * PREFER_BESIDE
      ? under
      : beside;
  return composed(taken.place, framed(taken.region));
}

/** Where a box in canvas coordinates is on screen under a frame. */
export function onScreen(box: Box, frame: Frame, pane: Box): Box {
  return {
    left: pane.left + frame.x + box.left * frame.zoom,
    top: pane.top + frame.y + box.top * frame.zoom,
    width: box.width * frame.zoom,
    height: box.height * frame.zoom,
  };
}

/** How far a card travels between two places. */
export function travel(
  from: { left: number; top: number },
  to: { left: number; top: number },
): number {
  return Math.hypot(to.left - from.left, to.top - from.top);
}

/** A move this short slides; a longer one fades out and in. */
export const SLIDE_MAX_PX = 160;
